import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ExternalLink, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import FlaggedPathsRepository from "../../repositories/FlaggedPathsRepository";
import {
  FlaggedPathOccurrence,
  FlaggedPathReasonLabels,
  FlaggedPathSummary,
  UpdateFlaggedPathOccurrence,
  UpdateFlaggedPathRequest,
} from "../../models/FlaggedPath";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import { useAuth } from "../../contexts/AuthContext";
import { SearchHighlight } from "../common/SearchHighlight";
import ActionConfirmationModal, { ACTION_CONFIGS } from "../common/ActionConfirmationModal";
import {
  describeSourceRecord,
  getEntityUrl,
  getMigrationEntityIdLabel,
  groupOccurrencesByEntity,
} from "./FlaggedPathsHelpers";
import { useRequiresReason } from "../../hooks/useRequiresReason";

/**
 * How many source records one page of the dialog holds. Well under the endpoint's own 1000 ceiling, because the
 * list is a scrolling column inside a dialog rather than a table an operator pages through quickly.
 */
const OCCURRENCE_PAGE_SIZE = 100;

interface FlaggedPathModalProps {
  open: boolean;
  flaggedPath: FlaggedPathSummary;
  organizationId: string;
  searchTerm?: string;
  onClose: () => void;
  onSave?: (request: UpdateFlaggedPathRequest) => Promise<void>;
}

const SystemWideDiscoveryBanner: React.FC<{
  allowDiscoverySystemWide: boolean;
  allowCasesDiscovery: boolean;
  allowIntakesDiscovery: boolean;
  allowProvidersDiscovery: boolean;
  allowFirmDocumentsFoldersDiscovery: boolean;
  allowTemplatesLibraryDiscovery: boolean;
}> = ({
  allowDiscoverySystemWide,
  allowCasesDiscovery,
  allowIntakesDiscovery,
  allowProvidersDiscovery,
  allowFirmDocumentsFoldersDiscovery,
  allowTemplatesLibraryDiscovery,
}) => {
  const enabledEntityTypes = [
    allowCasesDiscovery && "Cases",
    allowIntakesDiscovery && "Intakes",
    allowProvidersDiscovery && "Providers",
    allowFirmDocumentsFoldersDiscovery && "Firm Documents",
    allowTemplatesLibraryDiscovery && "Templates",
  ].filter(Boolean) as string[];

  if (!allowDiscoverySystemWide && enabledEntityTypes.length === 0) return null;

  return (
    <div className="p-4 border border-emerald-300 rounded-md bg-emerald-50 dark:bg-emerald-950/20">
      <span className="text-sm text-emerald-600 font-bold">
        Discovery Enabled
        {allowDiscoverySystemWide && " - System-wide discovery enabled"}
        {!allowDiscoverySystemWide && ` - Entity-specific discovery enabled: ${enabledEntityTypes.join(", ")}`}
      </span>
      <span className="text-xs text-emerald-600 mt-2 opacity-80 block">
        {allowDiscoverySystemWide
          ? "All individual entity settings are ignored across all entity types."
          : "All individual entity settings are ignored across enabled entity types."}
      </span>
    </div>
  );
};

/** One source record, named the way the source-links page names one. */
const SourceRecordRow: React.FC<{ occurrence: FlaggedPathOccurrence; searchTerm: string }> = ({
  occurrence,
  searchTerm,
}) => {
  const record = describeSourceRecord(occurrence);

  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-t border-border/60 first:border-t-0">
      <div className="min-w-0">
        {record ? (
          <div className="flex items-baseline gap-2 flex-wrap">
            <SearchHighlight text={record} searchTerm={searchTerm} level="body-sm" />
            {occurrence.sourceTabLabel && (
              <span className="text-xs text-muted-foreground">{occurrence.sourceTabLabel}</span>
            )}
          </div>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-muted-foreground">—</span>
              </TooltipTrigger>
              <TooltipContent>The source names no record for this link</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {occurrence.reason && (
          <span className="text-xs text-muted-foreground block mt-0.5">
            {FlaggedPathReasonLabels[occurrence.reason]}
            {occurrence.reasonDetail ? ` — ${occurrence.reasonDetail}` : ""}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {occurrence.isLive && occurrence.reopenedOn && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="warning" className="gap-1">
                    <RotateCcw className="size-3" />
                    Came back
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {`Cleared, then reported again on ${new Date(occurrence.reopenedOn).toLocaleDateString()}`}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {!occurrence.isLive && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="neutral">Cleared (inferred)</Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                No scan has reported this record since a cycle that covered its whole scope. Nothing on the wire
                can say a path is resolved, so every clearing is read from an absence.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
};

export const FlaggedPathModal: React.FC<FlaggedPathModalProps> = ({
  open,
  flaggedPath,
  organizationId,
  searchTerm = "",
  onClose,
  onSave,
}) => {
  const requiresReason = useRequiresReason();
  // Built during render, not inside queryFn: useApi() calls React hooks.
  const repository = new FlaggedPathsRepository();
  const { getUserInfo, hasManagedAccessToOrganization } = useAuth();
  const userInfo = getUserInfo();
  const isAssignedUser = hasManagedAccessToOrganization(organizationId);
  const canEdit = userInfo.isAdmin() || userInfo.isEditor();

  // Paged rather than fetched whole. A root folder every case in a firm reaches carries an occurrence per case,
  // which is why the endpoint pages at all; asking for no page size took the server's default fifty and showed
  // them as though they were the lot, leaving the per-entity checkbox for occurrence fifty-one unreachable.
  const [occurrencePage, setOccurrencePage] = useState(1);

  useEffect(() => setOccurrencePage(1), [flaggedPath.id, open]);

  const occurrencesQuery = useQuery({
    queryKey: [QUERY_KEYS.FLAGGED_PATH_OCCURRENCES, organizationId, flaggedPath.id, occurrencePage],
    queryFn: () =>
      repository.getFlaggedPathOccurrences(organizationId, flaggedPath.id, {
        pageNumber: occurrencePage,
        pageSize: OCCURRENCE_PAGE_SIZE,
      }),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    enabled: open,
  });

  const occurrences = occurrencesQuery.data?.data ?? [];
  const groups = groupOccurrencesByEntity(occurrences);
  const paging = occurrencesQuery.data?.paging ?? null;
  const totalOccurrences = paging?.totalCount ?? occurrences.length;

  const [allowDiscoverySystemWide, setAllowDiscoverySystemWide] = useState(flaggedPath.allowDiscoverySystemWide);
  const [allowCasesDiscovery, setAllowCasesDiscovery] = useState(flaggedPath.allowCasesDiscovery);
  const [allowIntakesDiscovery, setAllowIntakesDiscovery] = useState(flaggedPath.allowIntakesDiscovery);
  const [allowProvidersDiscovery, setAllowProvidersDiscovery] = useState(flaggedPath.allowProvidersDiscovery);
  const [allowFirmDocumentsFoldersDiscovery, setAllowFirmDocumentsFoldersDiscovery] =
    useState(flaggedPath.allowFirmDocumentsFoldersDiscovery);
  const [allowTemplatesLibraryDiscovery, setAllowTemplatesLibraryDiscovery] =
    useState(flaggedPath.allowTemplatesLibraryDiscovery);

  // Keyed by entity, not by occurrence: the decision is about the entity and the folder, so a case reached
  // through two source records must not end up allowed through one of them and blocked through the other.
  const [entityAllowances, setEntityAllowances] = useState<Record<string, boolean>>({});
  const [originalEntityAllowances, setOriginalEntityAllowances] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setAllowDiscoverySystemWide(flaggedPath.allowDiscoverySystemWide);
    setAllowCasesDiscovery(flaggedPath.allowCasesDiscovery);
    setAllowIntakesDiscovery(flaggedPath.allowIntakesDiscovery);
    setAllowProvidersDiscovery(flaggedPath.allowProvidersDiscovery);
    setAllowFirmDocumentsFoldersDiscovery(flaggedPath.allowFirmDocumentsFoldersDiscovery);
    setAllowTemplatesLibraryDiscovery(flaggedPath.allowTemplatesLibraryDiscovery);
  }, [flaggedPath]);

  useEffect(() => {
    const fetched: Record<string, boolean> = {};
    for (const group of groups) {
      if (!group.entityId) continue;
      fetched[group.entityId] = group.occurrences.some((o) => o.allowDiscovery);
    }

    // Merged rather than replaced. The page an operator has left still holds entities they toggled, and those
    // toggles are what handleSave diffs against; rebuilding from the current page alone would discard every
    // decision made before they turned it. An entity already known keeps the state it is in.
    setEntityAllowances((previous) => ({ ...fetched, ...previous }));
    setOriginalEntityAllowances((previous) => ({ ...fetched, ...previous }));
    // Rebuilt only when the fetched set changes; groups is derived from it each render.
  }, [occurrencesQuery.data]);

  const entityTypeState = (migrationType: MigrationType) => {
    switch (migrationType) {
      case MigrationType.CasesMigration:
      case MigrationType.CaseFoldersMigration:
        return { checked: allowCasesDiscovery, setChecked: setAllowCasesDiscovery, label: "Allow Cases discovery" };
      case MigrationType.IntakesMigration:
        return { checked: allowIntakesDiscovery, setChecked: setAllowIntakesDiscovery, label: "Allow Intakes discovery" };
      case MigrationType.ProvidersMigration:
      case MigrationType.ProviderFoldersMigration:
        return { checked: allowProvidersDiscovery, setChecked: setAllowProvidersDiscovery, label: "Allow Providers discovery" };
      case MigrationType.FirmDocumentsMigration:
        return {
          checked: allowFirmDocumentsFoldersDiscovery,
          setChecked: setAllowFirmDocumentsFoldersDiscovery,
          label: "Allow Firm Documents discovery",
        };
      case MigrationType.TemplatesMigration:
        return {
          checked: allowTemplatesLibraryDiscovery,
          setChecked: setAllowTemplatesLibraryDiscovery,
          label: "Allow Templates discovery",
        };
      default:
        return null;
    }
  };

  const hasChanges =
    allowDiscoverySystemWide !== flaggedPath.allowDiscoverySystemWide ||
    allowCasesDiscovery !== flaggedPath.allowCasesDiscovery ||
    allowIntakesDiscovery !== flaggedPath.allowIntakesDiscovery ||
    allowProvidersDiscovery !== flaggedPath.allowProvidersDiscovery ||
    allowFirmDocumentsFoldersDiscovery !== flaggedPath.allowFirmDocumentsFoldersDiscovery ||
    allowTemplatesLibraryDiscovery !== flaggedPath.allowTemplatesLibraryDiscovery ||
    Object.entries(entityAllowances).some(([entityId, allowed]) => originalEntityAllowances[entityId] !== allowed);

  const handleSave = async (reason?: string) => {
    if (!onSave) return;

    const changed: UpdateFlaggedPathOccurrence[] = Object.entries(entityAllowances)
      .filter(([entityId, allowed]) => originalEntityAllowances[entityId] !== allowed)
      .map(([entityId, allowDiscovery]) => ({ entityId, allowDiscovery }));

    await onSave({
      path: flaggedPath.path,
      allowDiscoverySystemWide,
      allowCasesDiscovery,
      allowIntakesDiscovery,
      allowProvidersDiscovery,
      allowFirmDocumentsFoldersDiscovery,
      allowTemplatesLibraryDiscovery,
      occurrences: changed,
      reason,
    });
  };

  /**
   * The non-modal save path, for Admins and assigned Editors. The confirmation modal emits its own toast via
   * ACTION_CONFIGS.UPDATE_FLAGGED_PATH; this covers the path that bypasses it.
   */
  const handleSaveDirect = async () => {
    try {
      await handleSave();
      toast.success("Flagged path updated");
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to update flagged path");
    }
  };

  const typeEnabled = (migrationType: MigrationType) =>
    allowDiscoverySystemWide || (entityTypeState(migrationType)?.checked ?? false);

  // Keyed on the control rather than on the migration type: CasesMigration and CaseFoldersMigration share one
  // checkbox and one piece of state, so a path reached through both rendered the same control twice with both
  // copies bound to the same setter — toggling one silently moved the other.
  const renderedControls = new Set<string>();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[800px] min-w-[600px]">
        <h4 className="text-lg font-semibold">{canEdit ? "Manage Flagged Path" : "Path Occurrences"}</h4>

        <div className="flex flex-col gap-4 mt-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Path</Label>
            <Input value={flaggedPath.path} readOnly className="bg-muted text-muted-foreground" />
          </div>

          <SystemWideDiscoveryBanner
            allowDiscoverySystemWide={allowDiscoverySystemWide}
            allowCasesDiscovery={allowCasesDiscovery}
            allowIntakesDiscovery={allowIntakesDiscovery}
            allowProvidersDiscovery={allowProvidersDiscovery}
            allowFirmDocumentsFoldersDiscovery={allowFirmDocumentsFoldersDiscovery}
            allowTemplatesLibraryDiscovery={allowTemplatesLibraryDiscovery}
          />

          {canEdit && (
            <div
              className={cn(
                "p-4 border border-border rounded-md bg-muted",
                allowDiscoverySystemWide !== flaggedPath.allowDiscoverySystemWide &&
                  "bg-amber-50 dark:bg-amber-950/20 border-amber-300"
              )}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allowDiscoverySystemWide}
                  onCheckedChange={(checked) => setAllowDiscoverySystemWide(checked as boolean)}
                />
                <Label className="text-sm">Allow discovery system-wide</Label>
              </div>
            </div>
          )}

          <span className="text-sm font-medium">
            {`${totalOccurrences} source ${totalOccurrences === 1 ? "record" : "records"}`}
            {occurrences.length < totalOccurrences
              ? ` — showing ${occurrences.length} across ${groups.length} ${groups.length === 1 ? "entity" : "entities"}`
              : ` across ${groups.length} ${groups.length === 1 ? "entity" : "entities"}`}
          </span>

          <div className="flex flex-col gap-2 max-h-[380px] overflow-auto pr-1">
            {occurrencesQuery.isLoading && (
              <span className="text-sm text-muted-foreground">Loading occurrences…</span>
            )}

            {!occurrencesQuery.isLoading && groups.length === 0 && (
              <span className="text-sm text-muted-foreground">
                Nothing references this path any more. The decision recorded above still stands if it is
                flagged again.
              </span>
            )}

            {groups.map((group) => {
              const typeState = entityTypeState(group.migrationType);
              const showTypeControl = canEdit && typeState && !renderedControls.has(typeState.label);
              if (showTypeControl) renderedControls.add(typeState!.label);

              const allowed = group.entityId ? entityAllowances[group.entityId] ?? false : false;
              const effectivelyAllowed = typeEnabled(group.migrationType) || allowed;
              const changed =
                group.entityId !== undefined && originalEntityAllowances[group.entityId] !== allowed;

              return (
                <React.Fragment key={group.key}>
                  {showTypeControl && (
                    <div className="flex items-center gap-2 mt-2 first:mt-0">
                      <Checkbox
                        checked={typeState.checked}
                        onCheckedChange={(checked) => typeState.setChecked(checked as boolean)}
                      />
                      <Label className="text-sm font-medium">{typeState.label}</Label>
                    </div>
                  )}

                  <div
                    className={cn(
                      "p-3 border border-border rounded-md bg-muted",
                      changed && "bg-amber-50 dark:bg-amber-950/20 border-amber-300"
                    )}
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {`${MigrationTypeLabels[group.migrationType]} · ${getMigrationEntityIdLabel(group.migrationType)}`}
                        </span>
                        {group.entityId ? (
                          <a
                            href={getEntityUrl(organizationId, group.migrationType, group.entityId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 no-underline hover:underline"
                          >
                            <SearchHighlight
                              text={group.migrationEntityId}
                              searchTerm={searchTerm}
                              level="body-sm"
                              color="primary"
                            />
                            <ExternalLink className="size-3.5" />
                          </a>
                        ) : (
                          <SearchHighlight
                            text={group.migrationEntityId}
                            searchTerm={searchTerm}
                            level="body-sm"
                          />
                        )}
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={effectivelyAllowed ? "success" : "danger"}>
                          {`Discovery ${effectivelyAllowed ? "Allowed" : "Blocked"}`}
                        </Badge>
                        {canEdit && group.entityId && (
                          <Checkbox
                            checked={allowed}
                            aria-label={`Allow discovery for ${group.migrationEntityId}`}
                            onCheckedChange={(checked) =>
                              setEntityAllowances((prev) => ({
                                ...prev,
                                [group.entityId!]: checked as boolean,
                              }))
                            }
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col">
                      {group.occurrences.map((occurrence) => (
                        <SourceRecordRow
                          key={occurrence.id}
                          occurrence={occurrence}
                          searchTerm={searchTerm}
                        />
                      ))}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {paging && paging.totalPages > 1 && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">
                {`Page ${paging.currentPage} of ${paging.totalPages}`}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Previous page of source records"
                  disabled={!paging.hasPrevious || occurrencesQuery.isFetching}
                  onClick={() => setOccurrencePage((page) => Math.max(1, page - 1))}
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Next page of source records"
                  disabled={!paging.hasNext || occurrencesQuery.isFetching}
                  onClick={() => setOccurrencePage((page) => page + 1)}
                >
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-row gap-4 justify-end">
            {canEdit ? (
              <>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                {requiresReason ? (
                  <ActionConfirmationModal
                    actionConfig={ACTION_CONFIGS.UPDATE_FLAGGED_PATH}
                    onConfirm={(reason) => handleSave(reason)}
                  >
                    <Button disabled={!hasChanges} variant={hasChanges ? "default" : "secondary"}>
                      <Save className="size-4" />
                      Save
                    </Button>
                  </ActionConfirmationModal>
                ) : (
                  <Button
                    onClick={handleSaveDirect}
                    disabled={!hasChanges}
                    variant={hasChanges ? "default" : "secondary"}
                  >
                    <Save className="size-4" />
                    Save
                  </Button>
                )}
              </>
            ) : (
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
